import apiClient from './apiClient';

export interface LocationResponse {
    id: string;
    name: string;
    description: string | null;
    address: string | null;
    isDefault: boolean;
    isActive: boolean;
}

export interface LocationCreateRequest {
    name: string;
    description?: string;
    address?: string;
    isDefault?: boolean;
}

export interface StockByLocation {
    locationId: string;
    locationName: string;
    quantity: number;
}

export interface ProductResponse {
    id: string;
    sku: string;
    name: string;
    description: string | null;
    category: string | null;
    unit: string;
    costPrice: number | null;
    salePrice: number | null;
    minStockAlert: number;
    barcode: string | null;
    supplierId?: string | null;
    supplierName?: string | null;
    totalStock: number;
    stockByLocation: StockByLocation[];
}

export interface ProductCreateRequest {
    sku: string;
    name: string;
    description?: string;
    category?: string;
    unit?: string;
    costPrice?: number;
    salePrice?: number;
    minStockAlert?: number;
    barcode?: string;
    supplierId?: string;
}

export interface InventoryStatsResponse {
    totalProducts: number;
    totalLocations: number;
    totalInventoryValue: number;
    lowStockCount: number;
    categories: string[];
}

export interface LowStockProductResponse {
    productId: string;
    name: string;
    sku: string;
    currentStock: number;
    minStockAlert: number;
    locationName: string;
}

export interface StockAddRequest {
    productId: string;
    locationId: string;
    quantity: number;
    notes?: string;
}

export interface StockTransferRequest {
    productId: string;
    fromLocationId: string;
    toLocationId: string;
    quantity: number;
    notes?: string;
}

export const inventoryApi = {
    // ==================== LOCATIONS ====================

    getLocations: async (salonId: string): Promise<LocationResponse[]> => {
        const response = await apiClient.get('/inventory/locations', {
            params: { salonId }
        });
        return response.data;
    },

    createLocation: async (salonId: string, request: LocationCreateRequest): Promise<LocationResponse> => {
        const response = await apiClient.post('/inventory/locations', request, {
            params: { salonId }
        });
        return response.data;
    },

    updateLocation: async (locationId: string, request: Partial<LocationCreateRequest>): Promise<LocationResponse> => {
        const response = await apiClient.put(`/inventory/locations/${locationId}`, request);
        return response.data;
    },

    deleteLocation: async (locationId: string): Promise<void> => {
        await apiClient.delete(`/inventory/locations/${locationId}`);
    },

    // ==================== PRODUCTS ====================

    getProducts: async (salonId: string): Promise<ProductResponse[]> => {
        const response = await apiClient.get('/inventory/products', {
            params: { salonId }
        });
        return response.data;
    },

    getProductById: async (productId: string): Promise<ProductResponse> => {
        const response = await apiClient.get(`/inventory/products/${productId}`);
        return response.data;
    },

    searchProducts: async (salonId: string, query: string): Promise<ProductResponse[]> => {
        const response = await apiClient.get('/inventory/products/search', {
            params: { salonId, query }
        });
        return response.data;
    },

    createProduct: async (salonId: string, request: ProductCreateRequest): Promise<ProductResponse> => {
        const response = await apiClient.post('/inventory/products', request, {
            params: { salonId }
        });
        return response.data;
    },

    updateProduct: async (productId: string, request: Partial<ProductCreateRequest>): Promise<ProductResponse> => {
        const response = await apiClient.put(`/inventory/products/${productId}`, request);
        return response.data;
    },

    deleteProduct: async (productId: string): Promise<void> => {
        await apiClient.delete(`/inventory/products/${productId}`);
    },

    // ==================== STOCK OPERATIONS ====================

    addStock: async (salonId: string, request: StockAddRequest): Promise<void> => {
        await apiClient.post('/inventory/stock/add', request, {
            params: { salonId }
        });
    },

    removeStock: async (salonId: string, request: StockAddRequest): Promise<void> => {
        await apiClient.post('/inventory/stock/remove', request, {
            params: { salonId }
        });
    },

    transferStock: async (salonId: string, request: StockTransferRequest): Promise<void> => {
        await apiClient.post('/inventory/stock/transfer', request, {
            params: { salonId }
        });
    },

    adjustStock: async (salonId: string, productId: string, locationId: string, newQuantity: number, reason?: string): Promise<void> => {
        await apiClient.post('/inventory/stock/adjust', {
            productId,
            locationId,
            newQuantity,
            reason
        }, {
            params: { salonId }
        });
    },

    getLowStockProducts: async (salonId: string): Promise<LowStockProductResponse[]> => {
        const response = await apiClient.get('/inventory/stock/low', {
            params: { salonId }
        });
        return response.data;
    },

    // ==================== STATS ====================

    getInventoryStats: async (salonId: string): Promise<InventoryStatsResponse> => {
        const response = await apiClient.get('/inventory/stats', {
            params: { salonId }
        });
        return response.data;
    }
};
